// SynthVoice (A13) — a procedural voice built from a manifest `patch`, exposing
// the same narrow surface AudioManager touches on an AudioBufferSourceNode
// (start/stop/loop/onended), so a synth asset slots into the EXACT same
// ownGain → [panner] → channel chain as a sample and inherits spatialization,
// era weighting, the mixer, mute/fades — everything (per synthesis.md: "an
// asset kind, not a separate system"). The patch vocabulary is the Sound Lab's
// proven palette expressed as data: osc/noise voices → optional filter →
// envelope, with one optional LFO.
//
// Graph:  [osc|noise] × N ──→ (filter?) ──→ envGain ──→ output
//                                 ▲              ▲
//                                 └──── lfo ─────┘  (target: gain|filter|pitch)
//
// Loops sustain until stop() (release ramp, then teardown). One-shots carry a
// `dur` and schedule their full envelope up front, firing onended at the end —
// the same contract a finished BufferSource has.

export interface SynthPatchVoice {
  type: 'osc' | 'noise';
  /** osc only */
  wave?: OscillatorType;
  /** osc only — Hz */
  freq?: number;
  /** osc only — cents (per-voice detune for beating pairs) */
  detune?: number;
  /** linear 0..1 within the voice mix */
  gain?: number;
}

export interface SynthPatch {
  voices: SynthPatchVoice[];
  filter?: { type: BiquadFilterType; freq: number; q?: number };
  /** attack/decay/release in seconds; sustain is a 0..1 level (default 1). */
  env?: { attack?: number; decay?: number; sustain?: number; release?: number };
  /** depth units depend on target: gain → 0..1 of the sustain level,
   *  filter → Hz around the filter freq, pitch → cents on every osc. */
  lfo?: { target: 'gain' | 'filter' | 'pitch'; rateHz: number; depth: number };
  /** One-shot length in seconds. Omit for sustained (loopable) patches. */
  dur?: number;
}

// One shared 2-second white-noise buffer per context (the classic looped-noise
// source) — regenerating it per voice would be pure GC churn.
const noiseBuffers = new WeakMap<AudioContext, AudioBuffer>();
function noiseBuffer(ctx: AudioContext): AudioBuffer {
  let buf = noiseBuffers.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseBuffers.set(ctx, buf);
  }
  return buf;
}

export class SynthVoice {
  /** Connect this into the manager's per-instance gain (the BufferSource slot). */
  readonly output: GainNode;
  loop: boolean;
  onended: (() => void) | null = null;

  private ctx: AudioContext;
  private patch: SynthPatch;
  private instanceDetune: number;
  private envGain: GainNode;
  private sources: Array<OscillatorNode | AudioBufferSourceNode> = [];
  private lfoOsc: OscillatorNode | null = null;
  private started = false;
  private stopped = false;
  private endTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(ctx: AudioContext, patch: SynthPatch, opts: { loop?: boolean; detune?: number } = {}) {
    this.ctx = ctx;
    this.patch = patch;
    this.instanceDetune = opts.detune ?? 0;
    // A patch with a dur is a one-shot regardless of the loop flag.
    this.loop = !!opts.loop && patch.dur === undefined;
    this.envGain = ctx.createGain();
    this.envGain.gain.value = 0;
    this.output = this.envGain;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    const { ctx, patch } = this;
    const now = ctx.currentTime;

    // Voice mix → (filter?) → envGain
    let mixTarget: AudioNode = this.envGain;
    let filter: BiquadFilterNode | null = null;
    if (patch.filter) {
      filter = ctx.createBiquadFilter();
      filter.type = patch.filter.type;
      filter.frequency.value = patch.filter.freq;
      filter.Q.value = patch.filter.q ?? 1;
      filter.connect(this.envGain);
      mixTarget = filter;
    }

    for (const v of patch.voices ?? []) {
      const vGain = ctx.createGain();
      vGain.gain.value = v.gain ?? 0.5;
      vGain.connect(mixTarget);
      if (v.type === 'noise') {
        const src = ctx.createBufferSource();
        src.buffer = noiseBuffer(ctx);
        src.loop = true;
        src.connect(vGain);
        src.start(now);
        this.sources.push(src);
      } else {
        const osc = ctx.createOscillator();
        osc.type = v.wave ?? 'sine';
        osc.frequency.value = v.freq ?? 440;
        try { osc.detune.value = (v.detune ?? 0) + this.instanceDetune; } catch { /* unsupported */ }
        osc.connect(vGain);
        osc.start(now);
        this.sources.push(osc);
      }
    }

    // LFO → its target param.
    if (patch.lfo) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = patch.lfo.rateHz;
      const lfoGain = ctx.createGain();
      lfo.connect(lfoGain);
      const sustain = patch.env?.sustain ?? 1;
      if (patch.lfo.target === 'gain') {
        lfoGain.gain.value = patch.lfo.depth * sustain; // ± depth of the sustain level
        lfoGain.connect(this.envGain.gain);
      } else if (patch.lfo.target === 'filter' && filter) {
        lfoGain.gain.value = patch.lfo.depth; // Hz
        lfoGain.connect(filter.frequency);
      } else if (patch.lfo.target === 'pitch') {
        lfoGain.gain.value = patch.lfo.depth; // cents
        for (const s of this.sources) {
          if ('detune' in s) { try { lfoGain.connect((s as OscillatorNode).detune); } catch { /* ignore */ } }
        }
      }
      lfo.start(now);
      this.lfoOsc = lfo;
    }

    // Envelope. attack → (decay to sustain) → [one-shot: release at dur-release].
    const env = patch.env ?? {};
    const attack = Math.max(0.001, env.attack ?? 0.01);
    const sustain = env.sustain ?? 1;
    const g = this.envGain.gain;
    g.setValueAtTime(0, now);
    g.linearRampToValueAtTime(1, now + attack);
    if (env.decay && sustain < 1) g.linearRampToValueAtTime(sustain, now + attack + env.decay);
    else if (sustain < 1) g.setValueAtTime(sustain, now + attack);

    if (!this.loop) {
      const dur = patch.dur ?? attack + (env.decay ?? 0) + (env.release ?? 0.1);
      const release = Math.min(env.release ?? 0.1, dur);
      g.setValueAtTime(g.value, now + Math.max(attack, dur - release));
      g.linearRampToValueAtTime(0, now + dur);
      // Fire onended exactly like a finished BufferSource. A JS timer is fine
      // here — this is voice CLEANUP, not scheduling (the A12/U7 frame-clock
      // rule governs when things FIRE, which stays with the engine).
      this.endTimer = setTimeout(() => this.teardown(true), dur * 1000 + 30);
    }
  }

  /** Same contract as BufferSource.stop(): immediate-ish stop. The manager's
   *  fade-out path ramps OUR downstream gain first, so a short release here
   *  only prevents a hard click when stop() arrives with no fade. */
  stop(): void {
    if (this.stopped || !this.started) { this.teardown(false); return; }
    const release = Math.min(this.patch.env?.release ?? 0.03, 0.06);
    const now = this.ctx.currentTime;
    const g = this.envGain.gain;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + release);
    if (this.endTimer) { clearTimeout(this.endTimer); this.endTimer = null; }
    this.endTimer = setTimeout(() => this.teardown(false), release * 1000 + 20);
    this.stopped = true;
  }

  private teardown(natural: boolean): void {
    if (this.endTimer) { clearTimeout(this.endTimer); this.endTimer = null; }
    for (const s of this.sources) { try { s.stop(); } catch { /* already */ } try { s.disconnect(); } catch { /* ignore */ } }
    this.sources = [];
    if (this.lfoOsc) { try { this.lfoOsc.stop(); } catch { /* already */ } try { this.lfoOsc.disconnect(); } catch { /* ignore */ } this.lfoOsc = null; }
    try { this.envGain.disconnect(); } catch { /* ignore */ }
    const cb = this.onended;
    this.onended = null;
    this.stopped = true;
    if (natural && cb) cb();
  }
}
